import React, { KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  getFontFamily,
  getFontSize,
  getLineHeight,
  getColor,
} from '../../../styles/themeGetters';

type StyledInputProps = {
  type: 'text' | 'number' | 'time' | 'date';
  placeholder?: string;
  disabled?: boolean;
  as: 'textarea' | 'input';
};

const disabledStyles = css`
  color: ${getColor('grey-light-100')};
  cursor: not-allowed;
  background: #fff;
`;

const textareaStyles = height => css`
  resize: none;
  height: ${height ?? '172px'};
  padding: 8px 0;
`;

const defaultStyles = css`
  height: 34px;
  padding: 0;
`;

const StyledInput = styled.input<StyledInputProps>`
  font-family: ${() => getFontFamily('lato')};
  font-size: ${getFontSize(14)};
  line-height: ${getLineHeight(14)};
  border: none;
  width: 100%;

  &:focus-visible {
    outline: none;
  }

  &::placeholder {
    color: ${getColor('grey-dark')};
    opacity: 1;
  }

  ${props => (props.disabled ? disabledStyles : null)};
  ${props =>
    props.as === 'textarea' ? textareaStyles(props.height) : defaultStyles};
`;

type ValidationRules = {
  required?: string;
  maxLength?:
    | {
        value: number;
        message: string;
      }
    | number;
  validate?: (value: string) => string | boolean;
};

type Props = {
  id: string;
  name: string;
  register?: (name: string, validation?: ValidationRules) => { ref };
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  autoFocus?: boolean;
  value?: string | number;
  validations?: ValidationRules;
  type?: 'text' | 'number' | 'time' | 'date';
  as?: 'input' | 'textarea';
  min?: string;
  max?: string;
  step?: string;
  height?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

const InputFieldBody = ({
  id,
  name,
  register,
  placeholder,
  disabled,
  onKeyDown,
  autoFocus,
  validations,
  type = 'text',
  as = 'input',
  min,
  max,
  step,
  height,
  value,
  onChange,
}: Props) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (onChange) {
      onChange(e);
    }

    // Run validation on change
    if (validations?.validate) {
      validations.validate(e.target.value);
    }
  };

  const inputProps = register
    ? {
        ...register(name, validations),
      }
    : {
        value,
        onChange: handleChange,
        maxLength:
          typeof validations?.maxLength === 'number'
            ? validations.maxLength
            : validations?.maxLength?.value,
        required: Boolean(validations?.required),
      };

  return (
    <StyledInput
      {...inputProps}
      placeholder={placeholder}
      disabled={disabled}
      onKeyDown={onKeyDown}
      autoFocus={autoFocus}
      type={type}
      as={as}
      id={id}
      min={min}
      max={max}
      step={step}
      height={height}
    />
  );
};

export { InputFieldBody };
